import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { DiffView } from "@/components/ui/diff-view";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConversationLink } from "@/components/molecules/ConversationLink";
import { cn, formatUrlForDisplay } from "@/lib/utils";
import { isDraftInProgress } from "@/lib/pageWorkflow";
import type {
  GA4InsightsGenerationJob,
  Idea,
  Page,
  WordPressCategory,
  WordPressVersion,
} from "@/schemas";
import { ChevronDownIcon, ExternalLink, PencilIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

type ManualVersionEdits = Pick<
  WordPressVersion,
  "wp_title" | "wp_slug" | "wp_excerpt" | "wp_content"
> & { wp_categories?: number[] };

interface PageExpandedSheetProps {
  page: Page | null;
  isOpen: boolean;
  onClose: () => void;
  versions: WordPressVersion[];
  isLoadingVersions?: boolean;
  versionError?: string | null;
  onLoadVersions?: (page: Page) => void;
  onEditPage?: (page: Page, customInstructions?: string) => Promise<void> | void;
  onApproveVersion?: (
    page: Page,
    version: WordPressVersion,
  ) => Promise<void> | void;
  onRunAudit?: (page: Page) => Promise<void> | void;
  linkedIdea?: Idea | null;
  onOpenLinkedIdea?: (page: Page) => void;
  isEditingPage?: boolean;
  versionActionLoading?: Record<string, boolean>;
  isRunningAudit?: boolean;
  onRefreshGAInsights?: (page: Page) => Promise<void> | void;
  isGaDomainConfigured?: boolean;
  isRefreshingGaInsights?: boolean;
  gaGenerationJob?: GA4InsightsGenerationJob | null;
  gaLatestInsightAt?: Date | null;
  onSaveManualVersion?: (
    page: Page,
    baseVersion: WordPressVersion,
    edits: ManualVersionEdits,
  ) => Promise<void> | void;
  isSavingManualVersion?: boolean;
  availableCategories?: WordPressCategory[];
}

type InspectorValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | InspectorValue[]
  | { [key: string]: InspectorValue };

function formatRelativeTime(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return "just now";
  if (diffMinutes < 60)
    return diffMinutes === 1 ? "1 minute ago" : `${diffMinutes} minutes ago`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24)
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  const diffDays = Math.floor(diffHours / 24);
  return diffDays === 1 ? "1 day ago" : `${diffDays} days ago`;
}

function formatTimestamp(
  timestamp: Date | { toDate?: () => Date } | null | undefined,
): string | null {
  if (!timestamp) return null;
  try {
    const date =
      timestamp instanceof Date
        ? timestamp
        : typeof timestamp.toDate === "function"
        ? timestamp.toDate()
        : new Date(timestamp as unknown as string);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

function extractCategoryIds(values: unknown[] | undefined): number[] {
  if (!values?.length) return [];
  const ids: number[] = [];
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      ids.push(value);
      continue;
    }
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      const candidate =
        typeof record.wp_category_id === "number"
          ? record.wp_category_id
          : typeof record.id === "number"
            ? record.id
            : null;
      if (candidate !== null && Number.isFinite(candidate)) {
        ids.push(candidate);
      }
    }
  }
  return ids;
}

function resolveCategoryNames(
  values: unknown[] | undefined,
  available: WordPressCategory[],
): string[] {
  if (!values?.length) return [];
  const byId = new Map(
    available.map((category) => [category.wp_category_id, category.name]),
  );
  const names: string[] = [];
  for (const value of values) {
    if (typeof value === "number" && Number.isFinite(value)) {
      const name = byId.get(value);
      if (name) names.push(name);
      continue;
    }
    if (value && typeof value === "object") {
      const record = value as Record<string, unknown>;
      const id =
        typeof record.wp_category_id === "number"
          ? record.wp_category_id
          : typeof record.id === "number"
            ? record.id
            : null;
      if (id !== null) {
        const name = byId.get(id);
        if (name) {
          names.push(name);
          continue;
        }
      }
      if (
        typeof record.name === "string" &&
        record.name.trim() !== ""
      ) {
        names.push(record.name);
      }
    }
  }
  return names;
}

function prettifyKey(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function sanitizeHtml(html: string): string {
  if (typeof window === "undefined") {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const blockedTags = [
    "script",
    "style",
    "iframe",
    "object",
    "embed",
    "link",
    "meta",
    "base",
  ];

  blockedTags.forEach((tag) => {
    doc.querySelectorAll(tag).forEach((node) => node.remove());
  });

  doc.querySelectorAll("*").forEach((element) => {
    for (const attribute of Array.from(element.attributes)) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim();

      if (name.startsWith("on")) {
        element.removeAttribute(attribute.name);
        continue;
      }

      if (
        ["href", "src", "xlink:href", "action", "formaction"].includes(name) &&
        /^javascript:/i.test(value)
      ) {
        element.removeAttribute(attribute.name);
      }
    }
  });

  return doc.body.innerHTML;
}

function getVersionLabel(version: WordPressVersion): string {
  const parts = [
    version.origin ? prettifyKey(version.origin) : null,
    version.review_status ? prettifyKey(version.review_status) : null,
    formatTimestamp(version.created_at),
  ].filter(Boolean);

  return parts.join(" • ") || version.id;
}

function extractHtml(version: WordPressVersion): string | null {
  if (version.wp_content) {
    return version.wp_content;
  }

  const rawContent = version.wp_raw_data?.content;
  if (
    rawContent &&
    typeof rawContent === "object" &&
    "rendered" in rawContent &&
    typeof rawContent.rendered === "string"
  ) {
    return rawContent.rendered;
  }

  return null;
}

function DynamicValue({
  label,
  value,
  depth = 0,
}: {
  label: string;
  value: InspectorValue;
  depth?: number;
}) {
  if (value == null || value === "") {
    return null;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return null;
    }

    return (
      <div className={cn("space-y-2", depth > 0 && "pl-4 border-l")}>
        <div className="text-xs font-medium text-foreground">{label}</div>
        <div className="space-y-2">
          {value.map((item, index) => (
            <DynamicValue
              key={`${label}-${index}`}
              label={`${label} ${index + 1}`}
              value={item}
              depth={depth + 1}
            />
          ))}
        </div>
      </div>
    );
  }

  if (typeof value === "object") {
    const entries = Object.entries(value).filter(
      ([, nestedValue]) => nestedValue != null && nestedValue !== "",
    );

    if (entries.length === 0) {
      return null;
    }

    return (
      <div className={cn("space-y-2", depth > 0 && "pl-4 border-l")}>
        <div className="text-xs font-medium text-foreground">{label}</div>
        <div className="space-y-3">
          {entries.map(([nestedKey, nestedValue]) => (
            <DynamicValue
              key={`${label}-${nestedKey}`}
              label={prettifyKey(nestedKey)}
              value={nestedValue as InspectorValue}
              depth={depth + 1}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-1">
      <div className="text-xs font-medium text-foreground">{label}</div>
      <div className="text-sm text-muted-foreground break-words">
        {String(value)}
      </div>
    </div>
  );
}

export function PageExpandedSheet({
  page,
  isOpen,
  onClose,
  versions,
  isLoadingVersions = false,
  versionError = null,
  onLoadVersions,
  onEditPage,
  onApproveVersion,
  onRunAudit,
  linkedIdea = null,
  onOpenLinkedIdea,
  isEditingPage = false,
  versionActionLoading = {},
  isRunningAudit = false,
  onRefreshGAInsights,
  isGaDomainConfigured = false,
  isRefreshingGaInsights = false,
  gaGenerationJob = null,
  gaLatestInsightAt = null,
  onSaveManualVersion,
  isSavingManualVersion = false,
  availableCategories = [],
}: PageExpandedSheetProps) {
  const [selectedVersionId, setSelectedVersionId] = useState<string>("");
  const [contentMode, setContentMode] = useState("preview");
  const [baseVersionId, setBaseVersionId] = useState<string>("");
  const [editInstructions, setEditInstructions] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editExcerpt, setEditExcerpt] = useState("");
  const [editContent, setEditContent] = useState("");
  const [editCategoryIds, setEditCategoryIds] = useState<number[]>([]);
  const titleRef = useRef<HTMLHeadingElement | null>(null);

  useEffect(() => {
    if (isOpen && page?.source === "wordpress") {
      onLoadVersions?.(page);
    }
  }, [isOpen, onLoadVersions, page]);

  const sortedVersions = useMemo(
    () =>
      [...versions].sort((a, b) => {
        const aDate = a.created_at?.toDate?.() ?? new Date(0);
        const bDate = b.created_at?.toDate?.() ?? new Date(0);
        return bDate.getTime() - aDate.getTime();
      }),
    [versions],
  );

  const defaultVersionId = useMemo(() => {
    if (sortedVersions.length === 0) {
      return "";
    }

    return sortedVersions[0]?.id ?? "";
  }, [sortedVersions]);

  const effectiveSelectedVersionId =
    selectedVersionId &&
    versions.some((version) => version.id === selectedVersionId)
      ? selectedVersionId
      : defaultVersionId;

  const selectedVersion = useMemo(
    () =>
      versions.find((version) => version.id === effectiveSelectedVersionId) ??
      null,
    [effectiveSelectedVersionId, versions],
  );

  const previewHtml = useMemo(() => {
    if (!selectedVersion) return null;
    const html = extractHtml(selectedVersion);
    return html ? sanitizeHtml(html) : null;
  }, [selectedVersion]);

  const baseVersion = useMemo(() => {
    if (sortedVersions.length < 2) return null;
    const idx = sortedVersions.findIndex(
      (v) => v.id === effectiveSelectedVersionId,
    );
    if (idx < 0) return null;
    if (baseVersionId && sortedVersions.some((v) => v.id === baseVersionId)) {
      return sortedVersions.find((v) => v.id === baseVersionId) ?? null;
    }
    if (idx + 1 >= sortedVersions.length) return null;
    return sortedVersions[idx + 1];
  }, [sortedVersions, effectiveSelectedVersionId, baseVersionId]);

  const diffOldContent = useMemo(() => {
    if (!baseVersion) return null;
    return extractHtml(baseVersion);
  }, [baseVersion]);

  const diffNewContent = useMemo(() => {
    if (!selectedVersion) return null;
    return extractHtml(selectedVersion);
  }, [selectedVersion]);
  const isAwaitingApproval =
    selectedVersion?.review_status === "ready_for_review";
  const isApprovingVersion = selectedVersion
    ? !!versionActionLoading[selectedVersion.id]
    : false;

  const summaryFields: Array<[string, InspectorValue]> = selectedVersion
    ? ([
        ["Version ID", selectedVersion.id],
        [
          "Version Origin",
          selectedVersion.origin && prettifyKey(selectedVersion.origin),
        ],
        [
          "Review Status",
          selectedVersion.review_status &&
            prettifyKey(selectedVersion.review_status),
        ],
        [
          "Publish Status",
          selectedVersion.publish_status &&
            prettifyKey(selectedVersion.publish_status),
        ],
        ["Title", selectedVersion.wp_title],
        ["Slug", selectedVersion.wp_slug],
        ["Type", selectedVersion.wp_type],
        ["Status", selectedVersion.wp_status],
        ["Link", selectedVersion.wp_link],
        ["Created", formatTimestamp(selectedVersion.created_at)],
        ["Synced", formatTimestamp(selectedVersion.synced_at)],
        ["Modified", formatTimestamp(selectedVersion.wp_modified)],
        ["Published", formatTimestamp(selectedVersion.published_at)],
      ] as Array<[string, InspectorValue]>).filter(([, value]) => value)
    : [];

  const metadataSections = selectedVersion
    ? [
        {
          key: "version",
          label: "Version Metadata",
          value: {
            wp_excerpt: selectedVersion.wp_excerpt,
            wp_comment_status: selectedVersion.wp_comment_status,
            wp_template: selectedVersion.wp_template,
            wp_author: selectedVersion.wp_author,
            generation_error: selectedVersion.generation_error,
            publish_error: selectedVersion.publish_error,
            approved_at: formatTimestamp(selectedVersion.approved_at),
          } as InspectorValue,
        },
        {
          key: "meta",
          label: "WordPress Meta",
          value: selectedVersion.wp_meta as InspectorValue,
        },
        {
          key: "raw",
          label: "Raw WordPress Data",
          value: selectedVersion.wp_raw_data as InspectorValue,
        },
      ]
    : [];

  if (!page) {
    return null;
  }

  const is_manual = page.source === "manual";
  const is_draft = page.source === "wordpress" && !page.url;
  const is_draft_in_progress = isDraftInProgress(
    page,
    versions,
    isLoadingVersions,
  );
  const is_selected_version_draft =
    is_draft ||
    (!!selectedVersion &&
      selectedVersion.origin !== "sync" &&
      selectedVersion.publish_status !== "published");
  const is_edit_draft_available = !is_manual && !is_draft && !!page.live_version_id;
  const canManualEdit =
    !is_manual && !!selectedVersion && !!onSaveManualVersion;
  // The Actions panel is writes end to end (edit draft, approve, audit, GA
  // insight generation) and core gates all four on min_role=editor. The parent
  // withholds the callbacks for viewers, leaving the panel with nothing to do.
  const canRunActions =
    !!onEditPage || !!onApproveVersion || !!onRunAudit || !!onRefreshGAInsights;

  const resetEditState = () => {
    setIsEditing(false);
    setEditTitle("");
    setEditSlug("");
    setEditExcerpt("");
    setEditContent("");
    setEditCategoryIds([]);
  };

  const startEditing = () => {
    if (!selectedVersion) return;
    setEditTitle(selectedVersion.wp_title ?? "");
    setEditSlug(selectedVersion.wp_slug ?? "");
    setEditExcerpt(selectedVersion.wp_excerpt ?? "");
    setEditContent(selectedVersion.wp_content ?? "");
    const knownIds = new Set(
      availableCategories.map((category) => category.wp_category_id),
    );
    setEditCategoryIds(
      extractCategoryIds(selectedVersion.wp_categories).filter((id) =>
        knownIds.has(id),
      ),
    );
    setIsEditing(true);
  };

  const cancelEditing = () => {
    resetEditState();
  };

  const toggleCategoryId = (categoryId: number, checked: boolean) => {
    setEditCategoryIds((current) => {
      if (checked) {
        return current.includes(categoryId) ? current : [...current, categoryId];
      }
      return current.filter((id) => id !== categoryId);
    });
  };

  const saveEdits = async () => {
    if (!selectedVersion || !onSaveManualVersion) return;
    await onSaveManualVersion(page, selectedVersion, {
      wp_title: editTitle,
      wp_slug: editSlug,
      wp_excerpt: editExcerpt,
      wp_content: editContent,
      wp_categories: editCategoryIds,
    });
    resetEditState();
    setSelectedVersionId("");
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          if (isSavingManualVersion) return;
          setSelectedVersionId("");
          setContentMode("preview");
          setBaseVersionId("");
          setEditInstructions("");
          resetEditState();
          onClose();
        }
      }}
    >
      <SheetContent
        side="bottom"
        className="h-[92dvh] sm:max-w-full overflow-y-auto p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          titleRef.current?.focus({ preventScroll: true });
        }}
      >
        <div className="flex h-full flex-col">
          <SheetHeader className="sticky top-0 z-10 border-b bg-background">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-2">
                <SheetTitle
                  ref={titleRef}
                  tabIndex={-1}
                  className="text-xl outline-none"
                >
                  {page.title || page.wordpress?.wp_slug || page.url}
                </SheetTitle>
                <SheetDescription>
                  {is_manual
                    ? "Run a heuristic audit for this manually tracked page."
                    : "Inspect WordPress versions, trigger an edit draft, or run a heuristic audit for this page."}
                </SheetDescription>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">
                    {is_manual ? "Manual" : "WordPress"}
                  </Badge>
                  {!is_manual && page.wordpress?.wp_type && (
                    <Badge variant="outline" className="capitalize">
                      {page.wordpress.wp_type_label || page.wordpress.wp_type}
                    </Badge>
                  )}
                  {!is_manual && page.wordpress?.wp_status && (
                    <Badge variant="secondary" className="capitalize">
                      {page.wordpress.wp_status}
                    </Badge>
                  )}
                  {!is_manual && page.live_version_id && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Badge className="max-w-[220px] truncate px-3">
                          Live {page.live_version_id}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent>
                        This is the last published version ID.
                      </TooltipContent>
                    </Tooltip>
                  )}
                  {!is_manual &&
                    resolveCategoryNames(
                      selectedVersion?.wp_categories,
                      availableCategories,
                    ).map((name) => (
                      <Tooltip key={name}>
                        <TooltipTrigger asChild>
                          <Badge variant="secondary" className="capitalize">
                            {name}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>Category</TooltipContent>
                      </Tooltip>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  <span title={page.url}>{formatUrlForDisplay(page.url)}</span>
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 hover:text-foreground"
                  >
                    Open
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  {page.updated_at && (
                    <span>Updated {formatTimestamp(page.updated_at)}</span>
                  )}
                </div>
                {!is_manual && (
                  <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center">
                    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                      Version
                    </div>
                    <div className="min-w-0 flex-1 sm:max-w-[520px]">
                      {isLoadingVersions ? (
                        <Skeleton className="h-9 w-full" />
                      ) : sortedVersions.length > 0 ? (
                        <Select
                          value={effectiveSelectedVersionId}
                          onValueChange={setSelectedVersionId}
                        >
                          <SelectTrigger className="w-full bg-background">
                            <SelectValue placeholder="Select a version" />
                          </SelectTrigger>
                          <SelectContent>
                            {sortedVersions.map((version) => (
                              <SelectItem key={version.id} value={version.id}>
                                {getVersionLabel(version)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          No versions available yet.
                        </div>
                      )}

                      {versionError && (
                        <div className="mt-2 text-sm text-destructive">
                          {versionError}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={cancelEditing}
                      disabled={isSavingManualVersion}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void saveEdits()}
                      disabled={isSavingManualVersion}
                    >
                      {isSavingManualVersion ? "Saving" : "Save"}
                    </Button>
                  </>
                ) : (
                  canManualEdit &&
                  (is_draft_in_progress ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="inline-block">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 pointer-events-none"
                            disabled
                            aria-label="Edit page draft"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        A draft is currently being generated. Wait until it's
                        ready before editing.
                      </TooltipContent>
                    </Tooltip>
                  ) : is_selected_version_draft ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={startEditing}
                          aria-label="Edit page draft"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit page draft</TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="inline-block">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 pointer-events-none"
                            disabled
                            aria-label="Edit page draft"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        Editing is available only for draft versions
                      </TooltipContent>
                    </Tooltip>
                  ))
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={onClose}
                  disabled={isSavingManualVersion}
                  aria-label="Close page details"
                >
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="flex-1 space-y-6 p-6">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,280px)_1fr]">
              <div
                className={cn(
                  "space-y-4",
                  isEditing && "opacity-50 pointer-events-none",
                )}
                aria-hidden={isEditing || undefined}
              >
                {canRunActions && (
                <div className="rounded-lg border p-4 space-y-3">
                  <div className="text-sm font-medium">Actions</div>
                  <div className="space-y-4">
                    <div
                      className={cn(
                        "space-y-3",
                        (!is_edit_draft_available || is_draft_in_progress) &&
                          "opacity-50",
                      )}
                    >
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Edit Content
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Create a new editable draft version for this page. Add
                        optional instructions to guide the update.
                      </p>
                      <Textarea
                        placeholder="Custom instructions for the next edit draft (optional)"
                        value={editInstructions}
                        onChange={(e) => setEditInstructions(e.target.value)}
                        rows={4}
                        disabled={
                          !is_edit_draft_available ||
                          isEditingPage ||
                          is_draft_in_progress
                        }
                      />
                      {is_manual ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-block">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled
                                className="pointer-events-none"
                              >
                                Edit Content
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Connect CMS to activate
                          </TooltipContent>
                        </Tooltip>
                      ) : is_draft_in_progress ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-block">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled
                                className="pointer-events-none"
                              >
                                Edit Content
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            A draft is currently being generated. Wait until
                            it's ready before starting another.
                          </TooltipContent>
                        </Tooltip>
                      ) : !is_edit_draft_available ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-block">
                              <Button
                                size="sm"
                                variant="outline"
                                disabled
                                className="pointer-events-none"
                              >
                                Edit Content
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Publish or approve a live WordPress version before
                            creating another edit draft.
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isEditingPage}
                          onClick={() => onEditPage?.(page, editInstructions)}
                        >
                          {isEditingPage ? "Creating Draft..." : "Edit Content"}
                        </Button>
                      )}
                    </div>

                    {isAwaitingApproval && selectedVersion && (
                      <>
                        <div className="h-px bg-border" />

                        <div className="space-y-3">
                          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            Approval
                          </div>
                          <p className="text-sm text-muted-foreground">
                            This selected draft is waiting for approval before it
                            can move forward.
                          </p>
                          <Button
                            size="sm"
                            disabled={isApprovingVersion}
                            onClick={() => onApproveVersion?.(page, selectedVersion)}
                          >
                            {isApprovingVersion
                              ? "Approving..."
                              : "Approve Draft"}
                          </Button>
                        </div>
                      </>
                    )}

                    <div className="h-px bg-border" />

                    <div
                      className={cn(
                        "space-y-3",
                        is_draft && "opacity-50",
                      )}
                    >
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Run Audit
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Start a new heuristic audit for the currently selected
                        page using its live URL.
                      </p>
                      {is_draft ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-block">
                              <Button
                                size="sm"
                                disabled
                                className="pointer-events-none"
                              >
                                Run Audit
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Audit is available only for published pages
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          size="sm"
                          disabled={isRunningAudit}
                          onClick={() => onRunAudit?.(page)}
                        >
                          {isRunningAudit ? "Running Audit..." : "Run Audit"}
                        </Button>
                      )}
                    </div>

                    <div className="h-px bg-border" />

                    <div
                      className={cn(
                        "space-y-3",
                        (is_draft || !isGaDomainConfigured) && "opacity-50",
                      )}
                    >
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                        Google Analytics Insights
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Generate GA conversion insights for this page.
                        {gaLatestInsightAt
                          ? ` Last refreshed ${formatRelativeTime(gaLatestInsightAt)}.`
                          : " Not yet generated."}
                      </p>
                      {is_draft ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-block">
                              <Button
                                size="sm"
                                disabled
                                className="pointer-events-none"
                              >
                                Refresh Insights
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Insights are available only for published pages
                          </TooltipContent>
                        </Tooltip>
                      ) : !isGaDomainConfigured ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span tabIndex={0} className="inline-block">
                              <Button
                                size="sm"
                                disabled
                                className="pointer-events-none"
                              >
                                Refresh Insights
                              </Button>
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Connect Google Analytics for this domain to
                            generate insights
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          size="sm"
                          disabled={isRefreshingGaInsights}
                          onClick={() => onRefreshGAInsights?.(page)}
                        >
                          {isRefreshingGaInsights
                            ? gaGenerationJob
                              ? `Generating… ${gaGenerationJob.completed_dimensions}/${gaGenerationJob.total_dimensions}`
                              : "Generating…"
                            : "Refresh Insights"}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
                )}

                <div className="rounded-lg border p-4 space-y-3">
                  <div className="text-sm font-medium">Source Idea</div>
                  {is_manual ? (
                    <p className="text-sm text-muted-foreground">
                      No data available
                    </p>
                  ) : page.idea_id ? (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Review the original idea that produced this WordPress
                        draft or page.
                      </p>
                      {linkedIdea ? (
                        <div className="rounded-md border bg-muted/30 p-3 text-sm">
                          <div className="font-medium text-foreground">
                            {linkedIdea.title}
                          </div>
                          {linkedIdea.target_keyword && (
                            <div className="mt-1 text-muted-foreground">
                              Keyword: {linkedIdea.target_keyword}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          Idea {page.idea_id} is not loaded in the current idea
                          list yet.
                        </div>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onOpenLinkedIdea?.(page)}
                      >
                        Open Source Idea
                      </Button>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No originating idea is linked to this page.
                    </p>
                  )}
                </div>

                <div className="rounded-lg border p-4 space-y-3">
                  <div className="text-sm font-medium">Page Summary</div>
                  <div className="space-y-3">
                    <DynamicValue label="Title" value={page.title} />
                    <DynamicValue label="URL" value={page.url} />
                    <DynamicValue
                      label="Input Keyword"
                      value={page.input_keyword}
                    />
                    <DynamicValue label="Idea ID" value={page.idea_id} />
                    <DynamicValue
                      label="Created"
                      value={formatTimestamp(page.created_at)}
                    />
                    <DynamicValue
                      label="WordPress"
                      value={page.wordpress as InspectorValue}
                    />
                    <DynamicValue
                      label="Custom Instructions"
                      value={page.custom_instructions as InspectorValue}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 min-w-0">
                {isEditing ? (
                  <div className="rounded-lg border p-4 space-y-4">
                    <div className="text-sm font-medium">Manual Edit</div>
                    <p className="text-sm text-muted-foreground">
                      Saving creates a new version forked from the currently
                      selected one. Existing versions are not modified.
                    </p>
                    <label className="grid gap-1">
                      <span className="text-xs font-medium text-foreground">
                        Title
                      </span>
                      <Input
                        value={editTitle}
                        onChange={(event) => setEditTitle(event.target.value)}
                        placeholder="Page title"
                        disabled={isSavingManualVersion}
                      />
                    </label>
                    <label className="grid gap-1">
                      <span className="text-xs font-medium text-foreground">
                        Slug
                      </span>
                      <Input
                        value={editSlug}
                        onChange={(event) => setEditSlug(event.target.value)}
                        placeholder="page-slug"
                        disabled={isSavingManualVersion}
                      />
                    </label>
                    <div className="grid gap-1">
                      <span className="text-xs font-medium text-foreground">
                        Categories
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={
                              isSavingManualVersion ||
                              availableCategories.length === 0
                            }
                            className="w-full justify-between font-normal"
                          >
                            <span className="truncate text-left">
                              {availableCategories.length === 0
                                ? "No categories available"
                                : editCategoryIds.length === 0
                                  ? "Select categories"
                                  : availableCategories
                                      .filter((category) =>
                                        editCategoryIds.includes(
                                          category.wp_category_id,
                                        ),
                                      )
                                      .map((category) => category.name)
                                      .join(", ")}
                            </span>
                            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          className="max-h-64 w-(--radix-dropdown-menu-trigger-width) overflow-y-auto"
                        >
                          <DropdownMenuLabel>Categories</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          {availableCategories.map((category) => (
                            <DropdownMenuCheckboxItem
                              key={category.wp_category_id}
                              checked={editCategoryIds.includes(
                                category.wp_category_id,
                              )}
                              onCheckedChange={(checked) =>
                                toggleCategoryId(
                                  category.wp_category_id,
                                  Boolean(checked),
                                )
                              }
                              onSelect={(event) => event.preventDefault()}
                            >
                              {category.name}
                            </DropdownMenuCheckboxItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <label className="grid gap-2">
                      <span className="text-sm font-medium">Excerpt</span>
                      <Textarea
                        value={editExcerpt}
                        onChange={(event) =>
                          setEditExcerpt(event.target.value)
                        }
                        placeholder="Short summary shown in lists and feeds"
                        rows={3}
                        disabled={isSavingManualVersion}
                      />
                    </label>
                    <label className="grid gap-2">
                      <span className="text-sm font-medium">Content</span>
                      <Textarea
                        value={editContent}
                        onChange={(event) =>
                          setEditContent(event.target.value)
                        }
                        placeholder="Page body (HTML)"
                        rows={18}
                        className="font-mono text-xs"
                        disabled={isSavingManualVersion}
                      />
                    </label>
                  </div>
                ) : is_manual ? (
                  <>
                    <div className="rounded-lg border p-4">
                      <div className="text-sm font-medium mb-2">
                        Content Preview
                      </div>
                      <p className="text-sm text-muted-foreground">
                        No data available
                      </p>
                    </div>

                    <div className="rounded-lg border p-4 space-y-4">
                      <div className="text-sm font-medium">
                        Selected Version
                      </div>
                      <p className="text-sm text-muted-foreground">
                        No data available
                      </p>
                    </div>

                    <div className="rounded-lg border p-4">
                      <div className="text-sm font-medium mb-2">
                        Dynamic Metadata
                      </div>
                      <p className="text-sm text-muted-foreground">
                        No data available
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <Tabs value={contentMode} onValueChange={setContentMode}>
                      <TabsList>
                        <TabsTrigger value="preview">Preview</TabsTrigger>
                        <TabsTrigger value="source">Source</TabsTrigger>
                        <TabsTrigger value="diff">Diff</TabsTrigger>
                      </TabsList>

                      <TabsContent
                        key={`preview-${effectiveSelectedVersionId || "none"}`}
                        value="preview"
                        className="space-y-4"
                      >
                        <div className="rounded-lg border">
                          <div className="border-b px-4 py-3">
                            <div className="text-sm font-medium">
                              Version Preview
                            </div>
                            {selectedVersion && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {getVersionLabel(selectedVersion)}
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            {isLoadingVersions && !selectedVersion ? (
                              <div className="space-y-3">
                                <Skeleton className="h-5 w-56" />
                                <Skeleton className="h-40 w-full" />
                              </div>
                            ) : previewHtml ? (
                              <div
                                className="prose prose-sm max-w-none [&_img]:max-w-full [&_table]:w-full"
                                dangerouslySetInnerHTML={{ __html: previewHtml }}
                              />
                            ) : (
                              <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                  No HTML preview is available for this version.
                                </p>
                                {selectedVersion?.wp_excerpt && (
                                  <div className="rounded-md bg-muted/50 p-4 text-sm text-muted-foreground">
                                    {selectedVersion.wp_excerpt}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent
                        key={`source-${effectiveSelectedVersionId || "none"}`}
                        value="source"
                        className="space-y-4"
                      >
                        <div className="rounded-lg border">
                          <div className="border-b px-4 py-3">
                            <div className="text-sm font-medium">
                              Render Source
                            </div>
                            {selectedVersion && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                {getVersionLabel(selectedVersion)}
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            <pre className="max-h-[40dvh] overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/50 p-4 text-xs">
                              {selectedVersion
                                ? extractHtml(selectedVersion) ||
                                  "No HTML source available."
                                : "No HTML source available."}
                            </pre>
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent
                        key={`diff-${effectiveSelectedVersionId || "none"}-${baseVersionId || "auto"}`}
                        value="diff"
                        className="space-y-4"
                      >
                        <div className="rounded-lg border">
                          <div className="border-b px-4 py-3 flex items-center justify-between gap-4">
                            <div>
                              <div className="text-sm font-medium">Version Diff</div>
                              <div className="mt-1 text-xs text-muted-foreground">
                                Comparing selected version against the previous version
                              </div>
                            </div>
                            {sortedVersions.length >= 2 && (
                              <Select
                                value={baseVersionId || "__auto"}
                                onValueChange={(val) =>
                                  setBaseVersionId(val === "__auto" ? "" : val)
                                }
                              >
                                <SelectTrigger className="w-[220px]">
                                  <SelectValue placeholder="Base version" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__auto">
                                    Auto (previous version)
                                  </SelectItem>
                                  {sortedVersions
                                    .filter((v) => v.id !== effectiveSelectedVersionId)
                                    .map((version) => (
                                      <SelectItem key={version.id} value={version.id}>
                                        {getVersionLabel(version)}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          <div className="p-4">
                            {isLoadingVersions && !selectedVersion ? (
                              <div className="space-y-3">
                                <Skeleton className="h-5 w-56" />
                                <Skeleton className="h-40 w-full" />
                              </div>
                            ) : diffOldContent && diffNewContent ? (
                              <DiffView
                                oldContent={diffOldContent}
                                newContent={diffNewContent}
                              />
                            ) : (
                              <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                  {!diffOldContent
                                    ? "No previous version available to compare against."
                                    : !diffNewContent
                                      ? "No HTML source available for the current version."
                                      : "Select a version to view differences."}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </TabsContent>
                    </Tabs>

                    <div className="rounded-lg border p-4 space-y-4">
                      <div className="text-sm font-medium">Selected Version</div>
                      {selectedVersion ? (
                        <>
                          <div className="grid gap-3 md:grid-cols-2">
                            {summaryFields.map(([label, value]) => (
                              <DynamicValue
                                key={label}
                                label={label}
                                value={value as InspectorValue}
                              />
                            ))}
                          </div>
                          {/* Per version, not per page: a chat-generated draft
                              sits in the same list as a WordPress sync and a
                              hand-made edit. */}
                          <ConversationLink
                            entity={selectedVersion}
                            className="inline-block"
                          />
                        </>
                      ) : (
                        <div className="text-sm text-muted-foreground">
                          Select a version to inspect its details.
                        </div>
                      )}
                    </div>

                    {selectedVersion && (
                      <div className="rounded-lg border p-4">
                        <div className="mb-2 text-sm font-medium">
                          Dynamic Metadata
                        </div>
                        <Accordion
                          type="multiple"
                          defaultValue={metadataSections.map(
                            (section) => section.key,
                          )}
                        >
                          {metadataSections.map((section) => (
                            <AccordionItem
                              key={section.key}
                              value={section.key}
                            >
                              <AccordionTrigger>
                                {section.label}
                              </AccordionTrigger>
                              <AccordionContent>
                                <DynamicValue
                                  label={section.label}
                                  value={section.value}
                                />
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
